/**
 * Context menu of the Lines panel (W2-07; DESIGN.md §4.10): acts on the selected lines (a right-click on an
 * unselected row selects it first), else offers adding and pasting. "Compare lines" runs W2-04a's
 * `columns.compare` (it pins one property of the selected lines as side-by-side columns). Other modules' commands
 * placed in the `lines.context` menu are appended.
 */

import type { AppServices, CommandDef } from '../../app/registry/types';
import type { LineId } from '../../core/model/types';
import type { I18n } from '../../i18n/types';
import type { MenuItem } from '../../ui/types';
import {
  changeTypeFlow,
  copyLines,
  cutLines,
  deleteFlow,
  duplicateLines,
  lockedReason,
  moveLinesBy,
  pasteLines,
  setGroup,
  setParentFlow,
  setZExtreme,
  toggleAlsoShow,
  toggleFilter,
  togglePinnedLanes,
  type FilterSet,
} from './lineActions';
import { m } from './lines.messages';
import { linesUi } from './linesUi';

type T = I18n['t'];

function allIn(set: ReadonlySet<LineId> | readonly LineId[], ids: readonly LineId[]): boolean {
  const has = (id: LineId): boolean => (Array.isArray(set) ? set.includes(id) : (set as ReadonlySet<LineId>).has(id));
  return ids.length > 0 && ids.every(has);
}

/** Ids of this module's commands, which the menu already offers in its own form. */
const OWN_PREFIX = 'lines.';

export function lineContextMenu(services: AppServices, t: T, targets: readonly LineId[]): MenuItem[] {
  const store = services.store;
  if (store === null) return [];
  const doc = store.doc;
  const st = services.editor.getState();
  const items: MenuItem[] = [];
  const add: MenuItem = {
    kind: 'submenu',
    label: t(m.menuAdd),
    items: [
      { kind: 'command', command: 'lines.add', label: t(m.cmdAddLine) },
      { kind: 'command', command: 'lines.addText', label: t(m.cmdAddText) },
      { kind: 'command', command: 'lines.addImage', label: t(m.cmdAddImage) },
      { kind: 'command', command: 'lines.addMany', label: t(m.cmdAddMany) },
    ],
  };
  const paste: MenuItem = {
    kind: 'action',
    label: t(m.cmdPaste),
    icon: 'ClipboardPaste',
    onSelect: () => {
      void pasteLines(services);
    },
  };
  if (targets.length === 0) {
    items.push(add, paste);
    return items;
  }
  const single = targets.length === 1 ? targets[0] : undefined;
  const locked = lockedReason(services, targets);
  const filter = (set: FilterSet, on: string, off: string, icon: string): MenuItem => ({
    kind: 'action',
    label: t(allIn(st.filters[set], targets) ? off : on),
    icon,
    onSelect: () => {
      toggleFilter(services, set, targets);
    },
  });

  if (single !== undefined) {
    items.push(
      {
        kind: 'action',
        label: t(m.cmdRename),
        icon: 'Pencil',
        disabledReason: locked,
        onSelect: () => {
          linesUi(services).set({ renaming: single });
        },
      },
      {
        kind: 'action',
        label: t(m.cmdChangeType),
        icon: 'Shapes',
        disabledReason: locked,
        onSelect: () => {
          void changeTypeFlow(services, single);
        },
      },
    );
  }
  const withParent = targets.filter((id) => (doc.lines.get(id)?.parent ?? null) !== null);
  items.push({
    kind: 'action',
    label: t(m.cmdSetParent),
    icon: 'Network',
    disabledReason: locked,
    onSelect: () => {
      linesUi(services).set({ modal: { kind: 'picker', mode: { kind: 'parent', lines: targets } } });
    },
  });
  if (withParent.length > 0) {
    items.push({
      kind: 'action',
      label: t(m.cmdDetach),
      icon: 'Unlink',
      disabledReason: locked,
      onSelect: () => {
        void setParentFlow(services, withParent, null);
      },
    });
  }
  items.push({ kind: 'separator' });
  const dup = (withNotes: boolean, withChildren: boolean, asChild: boolean) => (): void => {
    duplicateLines(services, targets, { withNotes, withChildren, asChild });
  };
  items.push({
    kind: 'submenu',
    label: t(m.menuDuplicate),
    items: [
      { kind: 'action', label: t(m.dupWithNotes), icon: 'Copy', onSelect: dup(true, false, false) },
      { kind: 'action', label: t(m.dupWithoutNotes), icon: 'CopyMinus', onSelect: dup(false, false, false) },
      { kind: 'action', label: t(m.dupWithChildren), icon: 'CopyPlus', onSelect: dup(true, true, false) },
      { kind: 'action', label: t(m.dupAsChild), icon: 'CornerDownRight', onSelect: dup(true, false, true) },
    ],
  });
  items.push(
    {
      kind: 'action',
      label: t(m.cmdCopy),
      icon: 'ClipboardCopy',
      onSelect: () => {
        void copyLines(services, targets);
      },
    },
    {
      kind: 'action',
      label: t(m.cmdCut),
      icon: 'Scissors',
      disabledReason: locked,
      onSelect: () => {
        void cutLines(services, targets);
      },
    },
    paste,
  );
  const groups = [...doc.groups.values()].sort((a, b) => a.order - b.order);
  items.push({
    kind: 'submenu',
    label: t(m.menuGroup),
    items: [
      {
        kind: 'action',
        label: t(m.groupNone),
        disabledReason: locked,
        onSelect: () => {
          setGroup(services, targets, null);
        },
      },
      ...groups.map((g): MenuItem => ({
        kind: 'action',
        label: g.name,
        icon: 'Tag',
        disabledReason: locked,
        onSelect: () => {
          setGroup(services, targets, g.id);
        },
      })),
      { kind: 'separator' },
      {
        kind: 'action',
        label: t(m.menuEditGroups),
        icon: 'Tags',
        onSelect: () => {
          linesUi(services).set({ modal: { kind: 'groups' } });
        },
      },
    ],
  });
  items.push({
    kind: 'submenu',
    label: t(m.menuOrder),
    items: [
      {
        kind: 'action',
        label: t(m.cmdToFront),
        icon: 'BringToFront',
        disabledReason: locked,
        onSelect: () => {
          setZExtreme(services, targets, 'front');
        },
      },
      {
        kind: 'action',
        label: t(m.cmdToBack),
        icon: 'SendToBack',
        disabledReason: locked,
        onSelect: () => {
          setZExtreme(services, targets, 'back');
        },
      },
      { kind: 'separator' },
      {
        kind: 'action',
        label: t(m.cmdMoveUp),
        icon: 'ArrowUp',
        disabledReason: locked,
        onSelect: () => {
          moveLinesBy(services, targets, -1);
        },
      },
      {
        kind: 'action',
        label: t(m.cmdMoveDown),
        icon: 'ArrowDown',
        disabledReason: locked,
        onSelect: () => {
          moveLinesBy(services, targets, 1);
        },
      },
    ],
  });
  items.push({ kind: 'separator' });
  items.push(
    filter('hiddenLines', m.toggleHide, m.toggleShow, 'EyeOff'),
    filter('soloLines', m.toggleSolo, m.toggleUnsolo, 'Focus'),
    filter('lockedLines', m.toggleLock, m.toggleUnlock, 'Lock'),
    filter('focusHitsoundLines', m.focusHitsoundsOn, m.focusHitsoundsOff, 'Volume2'),
    {
      kind: 'action',
      label: t(allIn(st.pinnedLanes, targets) ? m.unpinLane : m.pinLane),
      icon: 'Pin',
      onSelect: () => {
        togglePinnedLanes(services, targets);
      },
    },
    {
      kind: 'action',
      label: t(allIn(st.alsoShow, targets) ? m.alsoShowOff : m.alsoShowOn),
      icon: 'Layers',
      onSelect: () => {
        toggleAlsoShow(services, targets);
      },
    },
    { kind: 'command', command: 'columns.compare', label: t(m.compareLines) },
  );
  const extra: CommandDef[] = services.registry
    .menu('lines.context')
    .flatMap((g) => g.commands)
    .filter((c) => !c.id.startsWith(OWN_PREFIX) && c.id !== 'columns.compare');
  if (extra.length > 0) {
    items.push({ kind: 'separator' });
    for (const c of extra) items.push({ kind: 'command', command: c.id, label: t(c.title) });
  }
  items.push(
    { kind: 'separator' },
    {
      kind: 'action',
      label: t(m.menuDelete, { count: targets.length }),
      icon: 'Trash2',
      disabledReason: locked,
      onSelect: () => {
        void deleteFlow(services, targets);
      },
    },
  );
  return items;
}
